import { ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useViewportInsets } from '@/app/hooks/useViewportInsets';
import { AppInitializationController, InitializationState } from './AppInitializationController';
import { DialogController } from './DialogController';
import { Dialog } from '@/app/shared/ui/dialog/Dialog';
import { LoadingScreen } from './LoadingScreen';

interface AppInitializerProps {
  children: ReactNode;
}

/**
 * Компонент инициализации приложения
 * 
 * Выполняет последовательную инициализацию:
 * 1. Валидация пользователя через Telegram Init Data
 * 2. Подключение к WebSocket серверу
 * 
 * Отображает соответствующие экраны в зависимости от этапа инициализации:
 * - Экран загрузки во время валидации и подключения
 * - Экран ошибки при неудачной валидации
 * - Основной контент приложения после успешной инициализации
 * 
 * @param children - Дочерние компоненты, отображаемые после успешной инициализации
 */
export const AppInitializer = observer(({ children }: AppInitializerProps) => {
  const initController = AppInitializationController.getInstance();
  const dialogController = DialogController.getInstance();
  const { contentInsets } = useViewportInsets();
  const [showReadyFinalize, setShowReadyFinalize] = useState(false);
  const [readyFinalizeDone, setReadyFinalizeDone] = useState(false);

  // Инициализируем приложение при монтировании
  useEffect(() => {
    initController.initialize();

    return () => {
      initController.dispose();
    };
  }, [initController]);

  useLayoutEffect(() => {
    if (initController.state === InitializationState.READY) {
      setShowReadyFinalize(true);
      setReadyFinalizeDone(false);
      // Safety fallback: если по какой-то причине onReadyComplete не сработал,
      // не держим экран бесконечно.
      const timer = window.setTimeout(() => {
        setShowReadyFinalize(false);
        setReadyFinalizeDone(true);
      }, 1800);
      return () => window.clearTimeout(timer);
    }

    setShowReadyFinalize(false);
    setReadyFinalizeDone(false);
    return undefined;
  }, [initController.state]);

   const shouldShowInitializationScreen =
    initController.state === InitializationState.VALIDATING ||
    initController.state === InitializationState.CONNECTING ||
    initController.state === InitializationState.VALIDATION_ERROR ||
    (initController.state === InitializationState.READY && !readyFinalizeDone) ||
    showReadyFinalize;

  if (shouldShowInitializationScreen) {
        const errorMessage =
      initController.state === InitializationState.VALIDATION_ERROR
        ? initController.validationError
        : null;
    const loadingState =
      showReadyFinalize && initController.state === InitializationState.READY
        ? InitializationState.READY
        : initController.state;

    return (
      <>
        <LoadingScreen 
          state={loadingState}
          errorMessage={errorMessage}
          onRetry={initController.retry}
          onReadyComplete={() => {
            setShowReadyFinalize(false);
            setReadyFinalizeDone(true);
          }}
          contentInsets={contentInsets} 
        />
        <Dialog
          isOpen={dialogController.isOpen}
          title={dialogController.title}
          message={dialogController.message}
          buttons={dialogController.buttons}
          onClose={dialogController.closeDialog}
          closable={dialogController.closable}
        />
      </>
    );
  }

  return (
    <>
      {children}
      <Dialog
        isOpen={dialogController.isOpen}
        title={dialogController.title}
        message={dialogController.message}
        buttons={dialogController.buttons}
        onClose={dialogController.closeDialog}
        closable={dialogController.closable}
      />
    </>
  );
});
